import React from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Pagination = ({ 
  currentPage, 
  totalItems, 
  itemsPerPage = 20, 
  onPageChange 
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Don't show pagination if total items are less than items per page
  if (totalItems <= itemsPerPage) {
    return null;
  }

  const pageNumbers = [];
  const maxVisiblePages = 5;
  
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <nav aria-label="Page navigation" className="mt-4">
      <ul className="pagination justify-content-center">
        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
          <button
            className="page-link rounded-start"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <FaChevronLeft size={12} />
          </button>
        </li>
        
        {startPage > 1 && (
          <>
            <li className="page-item">
              <button 
                className="page-link" 
                onClick={() => onPageChange(1)}
                aria-label="Go to page 1"
              >
                1
              </button>
            </li>
            {startPage > 2 && (
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            )}
          </>
        )}

        {pageNumbers.map(number => (
          <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(number)}
              aria-label={`Go to page ${number}`}
              aria-current={currentPage === number ? 'page' : undefined}
            >
              {number}
            </button>
          </li>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <li className="page-item disabled">
                <span className="page-link">...</span>
              </li>
            )}
            <li className="page-item">
              <button 
                className="page-link" 
                onClick={() => onPageChange(totalPages)}
                aria-label={`Go to page ${totalPages}`}
              >
                {totalPages}
              </button>
            </li>
          </>
        )}

        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
          <button
            className="page-link rounded-end"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <FaChevronRight size={12} />
          </button>
        </li>
      </ul>
      <style jsx>{`
        .pagination {
          --bs-pagination-color: #0066cc;
          --bs-pagination-bg: #fff;
          --bs-pagination-border-color: #dee2e6;
          --bs-pagination-hover-color: #0052a3;
          --bs-pagination-hover-bg: #e9ecef;
          --bs-pagination-hover-border-color: #dee2e6;
          --bs-pagination-focus-color: #0052a3;
          --bs-pagination-focus-bg: #e9ecef;
          --bs-pagination-focus-border-color: #dee2e6;
          --bs-pagination-active-color: #fff;
          --bs-pagination-active-bg: #0066cc;
          --bs-pagination-active-border-color: #0066cc;
          --bs-pagination-disabled-color: #6c757d;
          --bs-pagination-disabled-bg: #fff;
          --bs-pagination-disabled-border-color: #dee2e6;
        }
        
        .page-link {
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          min-width: 36px;
          text-align: center;
          transition: all 0.2s ease-in-out;
        }
        
        .page-item.active .page-link {
          font-weight: 600;
          box-shadow: 0 2px 4px rgba(0, 102, 204, 0.2);
        }
        
        .page-link:hover:not(.disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .page-item.disabled .page-link {
          opacity: 0.5;
        }
      `}</style>
    </nav>
  );
};

export default Pagination; 